import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { 
  FaSearch, 
  FaCalendar, 
  FaUser, 
  FaArrowRight, 
  FaTag, 
  FaClock,
  FaEye,
  FaHeart,
  FaBookmark,
  FaShare,
  FaChartLine,
  FaCode,
  FaMobile,
  FaBrain,
  FaCloud
} from 'react-icons/fa';
import './BlogPage.css';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [viewMode, setViewMode] = useState('grid');

  const categories = [
    { id: 'all', name: 'All Posts', icon: null, count: 0 },
    { id: 'Web Development', name: 'Web Development', icon: FaCode, count: 0 },
    { id: 'Mobile Apps', name: 'Mobile Apps', icon: FaMobile, count: 0 },
    { id: 'AI/ML', name: 'AI/ML', icon: FaBrain, count: 0 },
    { id: 'Cloud Computing', name: 'Cloud Computing', icon: FaCloud, count: 0 }
  ];

  // Enhanced blog data with more details
  const blogPosts = [
    {
      slug: "future-of-web-development-2026",
      title: "Future of Web Development in 2026 (Complete Guide)",
      excerpt: "Discover the groundbreaking trends and technologies that will reshape web development in 2026. From AI-powered development to WebAssembly and beyond.",
      author: "Abhishek Yadav",
      authorAvatar: "https://ui-avatars.com/api/?name=Abhishek+Yadav&background=6366f1&color=fff",
      date: "March 5, 2024",
      readTime: "8 min read",
      category: "Web Development",
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085",
      views: 12450,
      likes: 342,
      tags: ["Web Development", "Trends", "2026", "AI"],
      featured: true
    },
    {
      slug: "build-mobile-app-react-native",
      title: "How to Build Mobile Apps with React Native (Step-by-Step)",
      excerpt: "A comprehensive guide to building production-ready mobile applications using React Native. Learn best practices, performance optimization, and deployment strategies.",
      author: "ZustandTech Team",
      authorAvatar: "https://ui-avatars.com/api/?name=ZustandTech+Team&background=6366f1&color=fff",
      date: "March 3, 2024",
      readTime: "12 min read",
      category: "Mobile Apps",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c",
      views: 8920,
      likes: 215,
      tags: ["React Native", "Mobile Development", "iOS", "Android"],
      featured: false
    },
    {
      slug: "ai-in-business-2026",
      title: "AI in Business: How Artificial Intelligence is Changing Everything",
      excerpt: "Explore how AI is revolutionizing business operations, from customer service automation to predictive analytics and decision-making processes.",
      author: "Dr. Sarah Johnson",
      authorAvatar: "https://ui-avatars.com/api/?name=Sarah+Johnson&background=6366f1&color=fff",
      date: "March 1, 2024",
      readTime: "10 min read",
      category: "AI/ML",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d",
      views: 15670,
      likes: 567,
      tags: ["Artificial Intelligence", "Machine Learning", "Business", "Automation"],
      featured: true
    },
    {
      slug: "cloud-migration-strategies",
      title: "Cloud Migration Strategies: A Complete Guide for Enterprises",
      excerpt: "Learn the essential strategies and best practices for migrating your enterprise applications to the cloud successfully.",
      author: "Michael Chen",
      authorAvatar: "https://ui-avatars.com/api/?name=Michael+Chen&background=6366f1&color=fff",
      date: "February 28, 2024",
      readTime: "15 min read",
      category: "Cloud Computing",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa",
      views: 5430,
      likes: 178,
      tags: ["Cloud", "AWS", "Azure", "Migration"],
      featured: false
    },
    {
      slug: "nextjs-vs-gatsby",
      title: "Next.js vs Gatsby: Which Framework Should You Choose in 2026?",
      excerpt: "A detailed comparison of the two most popular React frameworks to help you make the right choice for your next project.",
      author: "Emma Watson",
      authorAvatar: "https://ui-avatars.com/api/?name=Emma+Watson&background=6366f1&color=fff",
      date: "February 25, 2024",
      readTime: "9 min read",
      category: "Web Development",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c",
      views: 7230,
      likes: 289,
      tags: ["Next.js", "Gatsby", "React", "Framework"],
      featured: false
    },
    {
      slug: "machine-learning-python",
      title: "Machine Learning with Python: A Beginner's Guide",
      excerpt: "Start your journey into machine learning with this comprehensive guide covering essential libraries, algorithms, and real-world applications.",
      author: "Dr. James Wilson",
      authorAvatar: "https://ui-avatars.com/api/?name=James+Wilson&background=6366f1&color=fff",
      date: "February 22, 2024",
      readTime: "20 min read",
      category: "AI/ML",
      image: "https://images.unsplash.com/photo-1527474305487-b87b222841cc",
      views: 18900,
      likes: 734,
      tags: ["Python", "Machine Learning", "Data Science", "AI"],
      featured: true
    }
  ];

  // Update category counts
  categories.forEach(category => {
    if (category.id === 'all') {
      category.count = blogPosts.length;
    } else {
      category.count = blogPosts.filter(post => post.category === category.id).length;
    }
  });

  // Featured posts
  const featuredPosts = blogPosts.filter(post => post.featured);

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = blogPosts.filter(post => {
      const matchesSearch = 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = 
        selectedCategory === 'all' || post.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });

    // Apply sorting
    switch (sortBy) {
      case 'latest':
        return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
      case 'popular':
        return filtered.sort((a, b) => b.views - a.views);
      case 'trending':
        return filtered.sort((a, b) => b.likes - a.likes);
      default:
        return filtered;
    }
  }, [blogPosts, searchTerm, selectedCategory, sortBy]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="blog-page"
    >
      <Helmet>
        <title>Tech Blog | Web Development, AI & Software Insights | ZustandTech</title>
        <meta
          name="description"
          content="Read expert blogs on web development, mobile apps, AI, cloud computing and software trends. Stay updated with the latest in technology."
        />
        <meta name="keywords" content="tech blog, web development, AI, machine learning, cloud computing, mobile apps, programming" />
        <link rel="canonical" href="https://www.zustandtech.com/blog" />
        <meta property="og:title" content="Tech Blog | ZustandTech" />
        <meta property="og:description" content="Expert insights on web development, AI, and cloud computing" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.zustandtech.com/blog" />
      </Helmet>

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">Our Blog</span>
            <h1>
              Insights & Innovations
              <span className="gradient-text"> in Technology</span>
            </h1>
            <p>
              Explore expert articles, tutorials, and insights on the latest trends 
              in web development, artificial intelligence, cloud computing, and more.
            </p>
            <div className="hero-stats">
              <div className="stat">
                <FaChartLine />
                <span>{blogPosts.length}+ Articles</span>
              </div>
              <div className="stat">
                <FaUser />
                <span>10+ Experts</span>
              </div>
              <div className="stat">
                <FaClock />
                <span>Weekly Updates</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Posts Section */}
      {featuredPosts.length > 0 && (
        <section className="featured-posts">
          <div className="container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>Featured Articles</h2>
              <p>Hand-picked content you don't want to miss</p>
            </motion.div>
            <div className="featured-grid">
              {featuredPosts.map((post, index) => (
                <motion.div
                  key={post.slug}
                  className={`featured-card ${index === 0 ? 'primary' : ''}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="featured-image">
                    <img src={post.image} alt={post.title} loading="lazy" />
                    <div className="featured-overlay">
                      <span className="featured-category">{post.category}</span>
                    </div>
                  </div>
                  <div className="featured-content">
                    <div className="post-meta">
                      <span><FaUser /> {post.author}</span>
                      <span><FaCalendar /> {post.date}</span>
                      <span><FaClock /> {post.readTime}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="post-tags">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                    <Link to={`/blog/${post.slug}`} className="read-more">
                      Read Article <FaArrowRight />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Blog Section */}
      <section className="blog-main">
        <div className="container">
          {/* Filters Bar */}
          <div className="filters-bar">
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search articles by title, content, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  ×
                </button>
              )}
            </div>

            <div className="filters-controls">
              <div className="category-filters">
                {categories.map(category => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      {Icon && <Icon />}
                      {category.name}
                      <span className="count">{category.count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="sort-controls">
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="latest">Latest</option>
                  <option value="popular">Most Popular</option>
                  <option value="trending">Trending</option>
                </select>

                <div className="view-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                    </svg>
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Blog Posts Grid/List */}
          <AnimatePresence mode="wait">
            {filteredAndSortedPosts.length > 0 ? (
              <motion.div
                key={viewMode}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className={`blog-posts ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}
              >
                {filteredAndSortedPosts.map((post, index) => (
                  <motion.article
                    key={post.slug}
                    variants={itemVariants}
                    className="blog-post-card"
                  >
                    <div className="card-image">
                      <img src={post.image} alt={post.title} loading="lazy" />
                      <div className="card-badge">
                        <span>{post.category}</span>
                      </div>
                      <div className="card-actions">
                        <button className="action-btn" title="Save for later">
                          <FaBookmark />
                        </button>
                        <button className="action-btn" title="Share">
                          <FaShare />
                        </button>
                      </div>
                    </div>
                    <div className="card-content">
                      <div className="post-meta">
                        <div className="author-info">
                          <img src={post.authorAvatar} alt={post.author} className="author-avatar" />
                          <span className="author-name">{post.author}</span>
                        </div>
                        <div className="meta-stats">
                          <span><FaCalendar /> {post.date}</span>
                          <span><FaClock /> {post.readTime}</span>
                        </div>
                      </div>
                      <h3>
                        <Link to={`/blog/${post.slug}`}>
                          {post.title}
                        </Link>
                      </h3>
                      <p className="excerpt">{post.excerpt}</p>
                      <div className="card-footer">
                        <div className="tags">
                          {post.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="tag">#{tag}</span>
                          ))}
                        </div>
                        <div className="stats">
                          <span><FaEye /> {post.views.toLocaleString()}</span>
                          <span><FaHeart /> {post.likes}</span>
                        </div>
                      </div>
                      <Link to={`/blog/${post.slug}`} className="read-more-link">
                        Read Full Article <FaArrowRight />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            ) : (
              <motion.div 
                className="no-results"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <FaSearch className="no-results-icon" />
                <h3>No articles found</h3>
                <p>We couldn't find any articles matching your criteria.</p>
                <button onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }} className="reset-btn">
                  Clear all filters
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Load More Button */}
          {filteredAndSortedPosts.length > 0 && filteredAndSortedPosts.length < blogPosts.length && (
            <div className="load-more">
              <button className="load-more-btn">
                Load More Articles
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section">
        <div className="container">
          <motion.div 
            className="newsletter-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Subscribe to Our Newsletter</h2>
            <p>Get the latest tech insights and tutorials delivered straight to your inbox.</p>
            <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
              <input 
                type="email" 
                placeholder="Enter your email address"
                required
              />
              <button type="submit" className="subscribe-btn">
                Subscribe
              </button>
            </form>
            <p className="newsletter-note">
              No spam, unsubscribe at any time.
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default BlogPage;